import User from '@mdi/svg/svg/account-cog.svg?react';
import Impersonating from '@mdi/svg/svg/account-supervisor.svg?react';
import AccountSwitchIcon from '@mdi/svg/svg/account-switch.svg?react';
import Cog from '@mdi/svg/svg/cog.svg?react';
import HelpBoxIcon from '@mdi/svg/svg/help-box.svg?react';
import Keyboard from '@mdi/svg/svg/keyboard.svg?react';
import ThumbUp from '@mdi/svg/svg/thumb-up.svg?react';
import { MDIIcon } from '@solinkcorp/solink-components';
import DoorExit from '@solinkcorp/solink-components/svg/door-exit.svg?react';
import { isKiosk } from 'src/core/utils/kioskUtils';
import { getVersionString } from 'utils/BuildInfoUtil';
import FeaturesUtil from 'utils/FeaturesUtil';
import { MDI_VIEW_BOX } from 'vd-constants';
import { type MenuItem } from './UserDropDownMenu';

export interface UserMenuLocale {
  navigation: {
    settings: string;
    helpcenter: string;
    referral: string;
  };
  keyboard: {
    keyboardShortcuts: string;
  };
  auth: {
    switchUsers: string;
    logout: string;
  };
}

export interface BuildUserMenuItemsDeps {
  locale: UserMenuLocale;
  isImpersonating: boolean;
  userEmail: string;
  /** When true, omit Settings — it already lives in the sidebar actions row. */
  omitSettings?: boolean;
  /** When true, omit Help — it already lives in the sidebar actions row. */
  omitHelp?: boolean;
  onGoToSettings: () => void;
  onToggleKeyboardShortcuts: () => void;
  onGoToHelpCenter: () => void;
  onGoToReferral: () => void;
  onSwitchUsers: () => void;
  onGoToCallhomeUser: () => void;
  onLogout: () => void;
}

/**
 * Shared profile-menu model for the legacy header dropdown and the sidebar
 * profile popover. Presentation stays with each consumer.
 */
export function buildUserMenuItems({
  locale,
  isImpersonating,
  userEmail,
  omitSettings = false,
  omitHelp = false,
  onGoToSettings,
  onToggleKeyboardShortcuts,
  onGoToHelpCenter,
  onGoToReferral,
  onSwitchUsers,
  onGoToCallhomeUser,
  onLogout,
}: BuildUserMenuItemsDeps): MenuItem[] {
  const displaySettings = !omitSettings && FeaturesUtil.canViewSettings();
  const versionString = getVersionString();
  const isKioskMode = isKiosk();

  return [
    {
      icon: (
        <MDIIcon
          icon={isImpersonating ? Impersonating : User}
          size={20}
          fill={isImpersonating ? 'var(--color-orange)' : 'var(--color-primary)'}
        />
      ),
      id: 'user-email',
      type: 'label',
      text: userEmail || '',
    },
    { type: 'divider' },
    ...(isKioskMode
      ? [{ type: 'label' as const, text: versionString }, { type: 'divider' as const }]
      : []),
    ...(displaySettings
      ? [
          {
            icon: <MDIIcon icon={Cog} size={20} />,
            id: 'settings-nav-button',
            onClick: onGoToSettings,
            text: locale.navigation.settings,
            type: 'action' as const,
          },
        ]
      : []),
    ...(FeaturesUtil.isLiveViewOnly()
      ? []
      : [
          {
            icon: <MDIIcon icon={Keyboard} size={20} />,
            id: 'keyboard-shortcuts',
            onClick: onToggleKeyboardShortcuts,
            text: locale.keyboard.keyboardShortcuts,
            type: 'action' as const,
          },
        ]),
    ...(!omitHelp
      ? [
          {
            icon: <MDIIcon icon={HelpBoxIcon} size={20} />,
            id: 'help-center',
            isVisible: FeaturesUtil.canViewHelpCenter(),
            onClick: onGoToHelpCenter,
            text: locale.navigation.helpcenter,
            type: 'action' as const,
          },
        ]
      : []),
    { type: 'divider' },
    {
      icon: <MDIIcon icon={ThumbUp} size={20} />,
      id: 'referral',
      isVisible: FeaturesUtil.canViewReferral(),
      onClick: onGoToReferral,
      text: locale.navigation.referral,
      type: 'action',
    },
    ...(isImpersonating
      ? [
          {
            icon: (
              <AccountSwitchIcon
                className="icon icon-switch_user"
                viewBox={MDI_VIEW_BOX}
                width={21}
                height={21}
              />
            ),
            id: 'switch-users',
            onClick: onSwitchUsers,
            text: locale.auth.switchUsers,
            type: 'action' as const,
          },
        ]
      : []),
    ...(FeaturesUtil.isSolinkUser() || isImpersonating
      ? [
          {
            icon: <MDIIcon icon={Cog} size={20} />,
            id: 'callhome-user',
            className: 'internal-highlight',
            isInternal: true,
            onClick: onGoToCallhomeUser,
            text: 'User settings on CH',
            type: 'action' as const,
          },
        ]
      : []),
    {
      icon: <MDIIcon icon={DoorExit} size={20} />,
      id: 'logout-button',
      onClick: onLogout,
      text: locale.auth.logout || 'Logout',
      type: 'action',
    },
  ];
}
